import type { StatusColorTone } from '@/styles/statusColors';
import type { PcmpFailureStage } from '../types/pcmpProposal.types';
import type {
  PcmpAccountsBatchKind,
  PcmpAccountsBatchRollUp,
  PcmpAccountsBatchView,
  PcmpAccountsQueueCounts,
  PcmpAccountsTab,
  PcmpAccountsView,
} from '../types/pcmpAccounts.types';

export const PCMP_ACCOUNTS_DEFAULT_TAB: PcmpAccountsView = 'PENDING_BATCHES';

export const PCMP_ACCOUNTS_TAB_LABELS = {
  PENDING_BATCHES: 'Pending Batches',
  DISBURSED_BATCHES: 'Disbursed Batches',
  URGENT: 'Urgent',
  COMPLETED: 'Completed',
  DISBURSEMENT_FAILED: 'Disbursement Failed',
  RETURNED: 'Returned',
  RETURN_REQUESTS: 'Return Requests',
  REVERIFIED: 'Reverified',
  REDISBURSE_PENDING: 'Re-Disburse Pending',
  REDISBURSED: 'Re-Disbursed',
  ON_HOLD: 'On Hold',
} as const satisfies Record<PcmpAccountsView, string>;

/** The badge of each tab, from the counts endpoint; Return Requests counts from Requested Proposals. */
export const PCMP_ACCOUNTS_TAB_COUNT = {
  PENDING_BATCHES: (counts) => counts.pendingBatches,
  DISBURSED_BATCHES: (counts) => counts.disbursedBatches,
  URGENT: (counts) => counts.urgentPendingBatches,
  COMPLETED: (counts) => counts.completed,
  DISBURSEMENT_FAILED: (counts) => counts.disbursementFailed,
  RETURNED: (counts) => counts.returned,
  REVERIFIED: (counts) => counts.reverified,
  REDISBURSE_PENDING: (counts) => counts.redisbursePendingBatches,
  REDISBURSED: (counts) => counts.redisbursedBatches,
  ON_HOLD: (counts) => counts.onHold,
} as const satisfies Record<
  Exclude<PcmpAccountsView, 'RETURN_REQUESTS'>,
  (counts: PcmpAccountsQueueCounts) => number
>;

/** What each tab lists, and the API call that lists it. */
export type PcmpAccountsViewSource =
  | { kind: 'batches'; batchKind: PcmpAccountsBatchKind; view: PcmpAccountsBatchView }
  | { kind: 'items'; tab: PcmpAccountsTab }
  | { kind: 'redisburse'; view: PcmpAccountsBatchView }
  /** Accounts Payment Return requests, which Accounts staff decide (LDS-805). */
  | { kind: 'returnRequests' };

/** The Urgent tab's batch view comes from the URL, so it is resolved by the caller. */
export function pcmpAccountsViewSource(
  view: PcmpAccountsView,
  urgentView: PcmpAccountsBatchView,
): PcmpAccountsViewSource {
  switch (view) {
    case 'PENDING_BATCHES':
      return { kind: 'batches', batchKind: 'ROUTE', view: 'PENDING' };
    case 'DISBURSED_BATCHES':
      return { kind: 'batches', batchKind: 'ROUTE', view: 'DISBURSED' };
    case 'URGENT':
      return { kind: 'batches', batchKind: 'URGENT', view: urgentView };
    case 'REDISBURSE_PENDING':
      return { kind: 'redisburse', view: 'PENDING' };
    case 'REDISBURSED':
      return { kind: 'redisburse', view: 'DISBURSED' };
    case 'RETURN_REQUESTS':
      return { kind: 'returnRequests' };
    default:
      return { kind: 'items', tab: view };
  }
}

export const PCMP_ACCOUNTS_EMPTY_MESSAGE = {
  PENDING_BATCHES: 'No MIS batches are waiting for disbursement',
  DISBURSED_BATCHES: 'No disbursed batches found',
  URGENT: 'No urgent batches found',
  COMPLETED: 'No disbursed proposals found',
  DISBURSEMENT_FAILED: 'No failed disbursements',
  RETURNED: 'No returned payments',
  RETURN_REQUESTS: 'No Payment Return requests to decide',
  REVERIFIED: 'No corrected proposals waiting for re-disbursement',
  REDISBURSE_PENDING: 'No re-disbursement batches are pending',
  REDISBURSED: 'No re-disbursed batches found',
  ON_HOLD: 'No proposals on hold at Accounts',
} as const satisfies Record<PcmpAccountsView, string>;

export const PCMP_ACCOUNTS_BATCH_VIEW_LABELS = {
  PENDING: 'Pending',
  DISBURSED: 'Disbursed',
} as const satisfies Record<PcmpAccountsBatchView, string>;

export const PCMP_ACCOUNTS_BATCH_KIND_LABELS = {
  ROUTE: 'MIS batch',
  URGENT: 'Urgent batch',
} as const satisfies Record<PcmpAccountsBatchKind, string>;

export const PCMP_ACCOUNTS_ROLL_UP_TONE = {
  PENDING: 'warning',
  DISBURSEMENT_FAILED: 'danger',
  COMPLETED: 'success',
  DISBURSED: 'info',
} as const satisfies Record<PcmpAccountsBatchRollUp, StatusColorTone>;

export const PCMP_FAILURE_STAGE_LABELS = {
  PRE_PAYOUT: 'Before Disbursement',
  POST_PAYOUT: 'After Disbursement',
} as const satisfies Record<PcmpFailureStage, string>;

export const PCMP_ACCOUNTS_ACTION_COPY = {
  DISBURSE: { label: 'Disburse', title: 'Disburse', submit: 'Disburse', pending: 'Disbursing...' },
  FAIL: {
    label: 'Disbursement Failed',
    title: 'Mark Disbursement Failed',
    submit: 'Mark failed',
    pending: 'Saving...',
    toast: 'Marked Disbursement Failed',
  },
  RETURN: { label: 'Return' },
} as const;

export const PCMP_REDISBURSE_ACTION_COPY = {
  CREATE: {
    label: 'Create Re-Disbursement Batch',
    title: 'Create Re-Disbursement Batch',
    submit: 'Create batch',
    pending: 'Creating...',
  },
  DISBURSE: {
    label: 'Disburse',
    title: 'Re-Disburse',
    submit: 'Disburse',
    pending: 'Disbursing...',
  },
} as const;

/** A re-disbursement batch's member views: still to pay, or paid. */
export const PCMP_REDISBURSE_VIEW_LABELS = {
  PENDING: 'Pending',
  DISBURSED: 'Re-Disbursed',
} as const satisfies Record<PcmpAccountsBatchView, string>;
